/**
 * Vista previa del dashboard (Fase 3): KPIs con semáforo, estados, etapas y alertas con datos reales.
 * La Fase 4 agrega filtros, gráficos mensuales, panel de detalle, ficha 360° y modo presentación.
 */
import {
  KPI_STATUS_LABEL,
  alertCards,
  computeKpis,
  stageCards,
  statusCards,
  statusFromSheet,
  type Card,
  type KpiId,
  type RowsBySheet,
  type Snapshot,
} from '@redmil/shared';
import { formatValue } from '../format';
import { Icon, type IconName } from '../components/icons';

const STATUS_ICON: Record<string, IconName> = {
  'status:total': 'board',
  'status:Pendiente de autorización': 'hourglass',
  'status:Pendiente': 'clock',
  'status:En proceso': 'play',
  'status:Detenido': 'pause',
  'status:Terminado': 'check',
  'status:Entregado': 'truck',
};
const ALERT_ICON: Record<string, IconName> = {
  'alert:late': 'clock',
  'alert:ncr': 'alert',
  'alert:unsafe': 'shield',
  'alert:instruments': 'wrench',
  'alert:instrumentsSoon': 'wrench',
  'alert:stock': 'box',
  'alert:drawings': 'file',
  'alert:released': 'check',
  'alert:notReleased': 'lock',
};
const KPI_STATUS_ICON = { ok: '✓', bad: '✕', nodata: '–', nogoal: '!' } as const;

function StatCard({ card, icon }: { card: Card; icon: IconName }) {
  return (
    <article class={`stat tone-${card.tone}`}>
      <span class="stat-icon">
        <Icon name={icon} size={30} />
      </span>
      <div class="stat-body">
        <h3 class="stat-title">{card.title}</h3>
        <p class="stat-value">{card.items.length}</p>
        {(card.detail || card.global) && (
          <p class="stat-detail">
            {card.detail}
            {card.detail && card.global ? ' · ' : ''}
            {card.global ? 'Global' : ''}
          </p>
        )}
      </div>
    </article>
  );
}

export function Dashboard({ snapshot }: { snapshot: Snapshot }) {
  const data: RowsBySheet = Object.fromEntries(Object.entries(snapshot.sheets).map(([k, v]) => [k, v!.rows]));
  const goals = Object.fromEntries(snapshot.dashboard.map((d) => [d.id, { direction: d.direction, goal: d.goal }])) as Partial<
    Record<KpiId, { direction: string; goal: unknown }>
  >;
  const kpis = computeKpis(data, null, goals as never);
  const statuses = snapshot.lists.estado_ot ?? [];
  const stages = snapshot.lists.etapa ?? [];

  return (
    <>
      <section aria-labelledby="kpi-title">
        <h2 id="kpi-title" class="section-title">
          Indicadores del taller
        </h2>
        <div class="kpis">
          {snapshot.dashboard.map((d) => {
            const k = kpis.find((x) => x.id === d.id)!;
            if (d.restricted) {
              return (
                <article class="kpi kpi-restricted" key={d.id}>
                  <h3 class="kpi-name">{d.indicator}</h3>
                  <p class="kpi-value">
                    <Icon name="lock" size={22} /> Restringido
                  </p>
                  <p class="kpi-meta">Solo Administrador y Gerencia</p>
                </article>
              );
            }
            // Si la fórmula de la hoja cambió, se muestra el valor de la hoja (fuente de verdad).
            const value = d.formulaOk ? k.value : d.sheetValue;
            const status = d.formulaOk ? k.status : statusFromSheet(d.sheetStatus);
            return (
              <article class={`kpi kpi-${status}`} key={d.id} title={d.how}>
                <h3 class="kpi-name">{d.indicator}</h3>
                <p class="kpi-value">{formatValue(value, k.format)}</p>
                <p class="kpi-meta">
                  Meta {d.direction} {formatValue(d.goal, k.format)}
                  {k.global ? ' · Global' : ''}
                </p>
                <span class={`pill pill-${status}`}>
                  <span aria-hidden="true">{KPI_STATUS_ICON[status]}</span> {KPI_STATUS_LABEL[status]}
                </span>
                {!d.formulaOk && <p class="kpi-warn">Fórmula modificada en la hoja</p>}
              </article>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="status-title">
        <h2 id="status-title" class="section-title">
          Estado de tableros
        </h2>
        <div class="cards">
          {statusCards(data, null, statuses).map((c) => (
            <StatCard key={c.id} card={c} icon={STATUS_ICON[c.id] ?? 'board'} />
          ))}
        </div>
      </section>

      <section aria-labelledby="stage-title">
        <h2 id="stage-title" class="section-title">
          Tableros por etapa
        </h2>
        <div class="cards cards-compact">
          {stageCards(data, null, stages).map((c) => (
            <StatCard key={c.id} card={c} icon="stage" />
          ))}
        </div>
      </section>

      <section aria-labelledby="alert-title">
        <h2 id="alert-title" class="section-title">
          Alertas
        </h2>
        <div class="cards">
          {alertCards(data, null).map((c) => (
            <StatCard key={c.id} card={c} icon={ALERT_ICON[c.id] ?? 'alert'} />
          ))}
        </div>
      </section>
    </>
  );
}
